import React, { useState, useEffect } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import { User } from '../../../../types/models/user'
import { useUpdateUserMutation, useUpdateUserProfileMutation } from '../../../../services/userApi'
import { useGetOrganizationPublicQuery } from '../../../../services/orgsApi'
import { UpdateUserReq } from '../../../../types/api/api-user'
import { AdminCard } from '../styles'
import { useFetchUserProfileQuery } from '../../../../services/authApi'
import styled, { keyframes } from 'styled-components'
import { UserIcon, Mail, Calendar, Building2, Camera, Edit2 } from 'lucide-react'

const ProfileCard = styled(AdminCard)`
  margin-bottom: 2rem;
`

const ProfileTopSection = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 3rem;
  margin-bottom: 2rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`

const ProfileImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  flex-shrink: 0;
  width: 100%;
`

const AvatarContainer = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 1;
  }

  &:hover::after {
    opacity: 1;
  }

  &::before {
    content: 'Click to change';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 2;
    pointer-events: none;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  &:hover::before {
    opacity: 1;
  }
`

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
`

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`

const UsernameDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.1) 100%);
  border: 1px solid rgba(34, 211, 238, 0.25);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(34, 211, 238, 0.2);
  transition: all 0.25s ease;
  width: 100%;
  max-width: 240px;

  svg {
    color: #22d3ee;
    filter: drop-shadow(0 0 4px rgba(34, 211, 238, 0.5));
    transition: all 0.25s ease;
  }

  span {
    background: linear-gradient(
      90deg,
      #22d3ee 0%,
      #14b8a6 25%,
      #06b6d4 50%,
      #14b8a6 75%,
      #22d3ee 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.15) 100%);
    border-color: rgba(34, 211, 238, 0.4);
    box-shadow: 0 6px 16px rgba(34, 211, 238, 0.3);
    transform: translateY(-2px);

    svg {
      filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.7));
      transform: scale(1.1);
    }

    span {
      animation-duration: 2s;
    }
  }
`

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #14b8a6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(34, 211, 238, 0.4);
  border: 4px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`

const AvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
  cursor: pointer;
  border-radius: 50%;

  &:hover {
    opacity: 1;
  }
`

const ProfileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(34, 211, 238, 0.03);
  border: 1px solid rgba(34, 211, 238, 0.12);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.05);
    border-color: rgba(34, 211, 238, 0.2);
  }
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(34, 211, 238, 0.15);
`

const SectionTitle = styled.h6`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
`

const SectionEditButton = styled.button`
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.25);
  color: #22d3ee;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(34, 211, 238, 0.2);
    border-color: rgba(34, 211, 238, 0.4);
    transform: translateY(-1px);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const InfoList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const InfoLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`

const InfoValue = styled.div`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  word-break: break-word;

  &.monospace {
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    font-weight: 500;
  }
`

const StyledFormControl = styled(Form.Control)`
  background: rgba(34, 211, 238, 0.05) !important;
  border: 1px solid rgba(34, 211, 238, 0.25) !important;
  color: #ffffff !important;
  border-radius: 12px;
  padding: 0.875rem 1.25rem;
  transition: all 0.25s ease;
  font-size: 0.95rem;

  &:focus {
    background: rgba(34, 211, 238, 0.1) !important;
    border-color: rgba(34, 211, 238, 0.5) !important;
    color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.2), 0 4px 12px rgba(34, 211, 238, 0.15) !important;
    transform: translateY(-1px);
    outline: none !important;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
  }

  &:hover:not(:focus) {
    border-color: rgba(34, 211, 238, 0.35) !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(34, 211, 238, 0.02) !important;
  }
`

const FormLabel = styled(Form.Label)`
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HiddenFileInput = styled.input`
  display: none;
`

interface ProfileTabProps {
  currentUser: User | undefined
  formatDate: (date: string) => string
}

const ProfileTab: React.FC<ProfileTabProps> = ({ currentUser, formatDate }) => {
  const [updateUser] = useUpdateUserMutation()
  const [updateUserProfile] = useUpdateUserProfileMutation()
  const { data: orgsData } = useGetOrganizationPublicQuery()
  const { refetch: refetchProfile } = useFetchUserProfileQuery()

  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false)
  const [isEditingAccountInfo, setIsEditingAccountInfo] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<UpdateUserReq>({
    email: '',
    first_name: '',
    last_name: '',
    org_id: '',
  })
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const profileImage =
    currentUser?.profile_400 || currentUser?.profile_200 || currentUser?.profile_100

  useEffect(() => {
    if (currentUser) {
      setFormData({
        email: currentUser.email || '',
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        org_id: currentUser.org_id || '',
      })
    }
  }, [currentUser])

  const handleSave = async (section: 'personal' | 'account') => {
    if (!currentUser) return
    setIsLoading(true)
    try {
      await updateUser({
        id: currentUser.id,
        userData: formData,
      }).unwrap()
      await refetchProfile()
      if (section === 'personal') {
        setIsEditingPersonalInfo(false)
      } else {
        setIsEditingAccountInfo(false)
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = (section: 'personal' | 'account') => {
    if (currentUser) {
      setFormData({
        email: currentUser.email || '',
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        org_id: currentUser.org_id || '',
      })
    }
    if (section === 'personal') {
      setIsEditingPersonalInfo(false)
    } else {
      setIsEditingAccountInfo(false)
    }
  }

  const handleStartEdit = (section: 'personal' | 'account') => {
    if (currentUser) {
      setFormData({
        email: currentUser.email || '',
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        org_id: currentUser.org_id || '',
      })
    }
    if (section === 'personal') {
      setIsEditingPersonalInfo(true)
    } else {
      setIsEditingAccountInfo(true)
    }
  }

  const handleProfileUpload = async (file: File) => {
    if (!currentUser) return
    try {
      await updateUserProfile({ id: currentUser.id, file }).unwrap()
      await refetchProfile()
    } catch (err) {
      console.error('Failed to upload profile:', err)
    }
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  if (!currentUser) return null

  return (
    <ProfileCard>
      <div
        className="card-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h5
          className="mb-0"
          style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <UserIcon size={20} />
          My Account
        </h5>
      </div>
      <div className="card-body">
        <ProfileTopSection>
          <ProfileImageSection>
            <AvatarContainer onClick={handleAvatarClick}>
              {profileImage ? (
                <img
                  src={`data:image/;base64,${profileImage}`}
                  alt="Profile"
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    border: '4px solid rgba(34, 211, 238, 0.3)',
                    objectFit: 'cover',
                    boxShadow: '0 8px 24px rgba(34, 211, 238, 0.4)',
                  }}
                />
              ) : (
                <ProfileAvatar
                  style={{
                    width: '200px',
                    height: '200px',
                    fontSize: '5rem',
                  }}
                >
                  {currentUser.first_name?.[0] || <UserIcon size={100} />}
                </ProfileAvatar>
              )}
              <AvatarOverlay>
                <Camera size={40} color="white" />
              </AvatarOverlay>
            </AvatarContainer>
            <ProfileHeader>
              <UsernameDisplay>
                <UserIcon size={20} />
                <span>{currentUser.username}</span>
              </UsernameDisplay>
            </ProfileHeader>
          </ProfileImageSection>

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement).files?.[0]
              if (file) {
                handleProfileUpload(file)
              }
            }}
          />

          <ProfileSection style={{ flex: 1 }}>
            <SectionHeader>
              <SectionTitle>
                <UserIcon size={18} />
                Personal Information
              </SectionTitle>
              {!isEditingPersonalInfo ? (
                <SectionEditButton onClick={() => handleStartEdit('personal')}>
                  <Edit2 size={14} />
                  Edit
                </SectionEditButton>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <SectionEditButton
                    onClick={() => handleCancel('personal')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    Cancel
                  </SectionEditButton>
                  <SectionEditButton
                    onClick={() => handleSave('personal')}
                    disabled={isLoading}
                    style={{
                      background: 'rgba(34, 211, 238, 0.2)',
                      border: '1px solid rgba(34, 211, 238, 0.4)',
                    }}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </SectionEditButton>
                </div>
              )}
            </SectionHeader>
            {!isEditingPersonalInfo ? (
              <InfoList>
                <InfoRow>
                  <InfoLabel>
                    <UserIcon size={14} />
                    First Name
                  </InfoLabel>
                  <InfoValue>{currentUser.first_name || '—'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    <UserIcon size={14} />
                    Last Name
                  </InfoLabel>
                  <InfoValue>{currentUser.last_name || '—'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    <Mail size={14} />
                    Email
                  </InfoLabel>
                  <InfoValue>{currentUser.email}</InfoValue>
                </InfoRow>
                {currentUser.org_id && (
                  <InfoRow>
                    <InfoLabel>
                      <Building2 size={14} />
                      Organization
                    </InfoLabel>
                    <InfoValue>{orgsData?.name || 'Loading...'}</InfoValue>
                  </InfoRow>
                )}
              </InfoList>
            ) : (
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <FormLabel>
                        <UserIcon size={16} />
                        First Name
                      </FormLabel>
                      <StyledFormControl
                        type="text"
                        value={formData.first_name || ''}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        placeholder="First Name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <FormLabel>
                        <UserIcon size={16} />
                        Last Name
                      </FormLabel>
                      <StyledFormControl
                        type="text"
                        value={formData.last_name || ''}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        placeholder="Last Name"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <FormLabel>
                    <Mail size={16} />
                    Email
                  </FormLabel>
                  <StyledFormControl
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </Form.Group>
              </Form>
            )}
          </ProfileSection>
        </ProfileTopSection>

        <ProfileContent>
          <ProfileSection>
            <SectionHeader>
              <SectionTitle>
                <Calendar size={18} />
                Account Information
              </SectionTitle>
              {!isEditingAccountInfo ? (
                <SectionEditButton onClick={() => handleStartEdit('account')}>
                  <Edit2 size={14} />
                  Edit
                </SectionEditButton>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <SectionEditButton
                    onClick={() => handleCancel('account')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    Cancel
                  </SectionEditButton>
                  <SectionEditButton
                    onClick={() => handleSave('account')}
                    disabled={isLoading}
                    style={{
                      background: 'rgba(34, 211, 238, 0.2)',
                      border: '1px solid rgba(34, 211, 238, 0.4)',
                    }}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </SectionEditButton>
                </div>
              )}
            </SectionHeader>
            {!isEditingAccountInfo ? (
              <InfoList>
                <InfoRow>
                  <InfoLabel>User ID</InfoLabel>
                  <InfoValue className="monospace">{currentUser.id}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    <Calendar size={14} />
                    Account Created
                  </InfoLabel>
                  <InfoValue>{formatDate(currentUser.created_at)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>
                    <Calendar size={14} />
                    Last Login
                  </InfoLabel>
                  <InfoValue>{formatDate(currentUser.last_login || '')}</InfoValue>
                </InfoRow>
                {currentUser.last_password_change && (
                  <InfoRow>
                    <InfoLabel>
                      <Calendar size={14} />
                      Last Password Change
                    </InfoLabel>
                    <InfoValue>{formatDate(currentUser.last_password_change)}</InfoValue>
                  </InfoRow>
                )}
              </InfoList>
            ) : (
              <Form>
                <Form.Group className="mb-3">
                  <FormLabel>
                    <Calendar size={16} />
                    Account Created
                  </FormLabel>
                  <StyledFormControl
                    type="text"
                    value={formatDate(currentUser.created_at)}
                    disabled
                  />
                </Form.Group>
              </Form>
            )}
          </ProfileSection>
        </ProfileContent>
      </div>
    </ProfileCard>
  )
}

export default ProfileTab
